import type { GetStaticPaths, GetStaticProps } from "next";
import TreatmentPage, {
  getStaticPaths as enGetStaticPaths,
  getStaticProps as enGetStaticProps,
} from "@/pages/[treatment]";
import I18nProvider from "@/components/I18nProvider";
import { translateData } from "@/lib/translation-memory";
import type { Treatment, NavNode } from "@/lib/treatments";
import type { BlogPost } from "@/lib/blog";

// Arabic treatment pages. Unlike the static pages, most of the copy here arrives
// as props from the CMS rather than as literal JSX, and Layout's tree walk
// cannot reach inside TreatmentSections/TreatmentSidebar (they are components,
// not markup). So the data itself is translated here in getStaticProps.

type Props = { treatment: Treatment; nav: NavNode[]; recentPosts: BlogPost[] };

// Values that must survive untranslated: they are identifiers, paths or dates.
const SKIP_KEYS = new Set([
  "slug",
  "href",
  "media_id",
  "name",
  "image",
  "hero_image",
  "imageSide",
  "type",
  "html",
  "date",
  "createdDate",
  "order",
  "status",
  "parent",
]);

export default function ArabicTreatmentPage(props: Props) {
  return (
    <I18nProvider locale="ar">
      <TreatmentPage {...props} />
    </I18nProvider>
  );
}

export const getStaticPaths: GetStaticPaths = async (ctx) => {
  const result = await enGetStaticPaths(ctx);
  return result;
};

export const getStaticProps: GetStaticProps<Props, { treatment: string }> = async (ctx) => {
  const result = await enGetStaticProps(ctx);
  if (!("props" in result)) return result;

  const props = (await result.props) as Props;

  return {
    props: {
      ...props,
      treatment: translateData(props.treatment, "ar", SKIP_KEYS),
      // Menu labels are translated too, but hrefs stay as the English slugs —
      // localizeHref adds the /hi prefix at render time.
      nav: translateData(props.nav, "ar", SKIP_KEYS),
      // Blog posts are deliberately left in their original language.
      recentPosts: props.recentPosts,
    },
  };
};
